// Google Fonts Dictionary - Real data from Google Fonts API
import type { FontDictionary } from '../types/fonts';
import { deriveWeight, deriveStyle } from './utils';
import { validate } from 'class-validator';
import { plainToClass } from 'class-transformer';
import { GoogleFontsResponse } from '../dto/GoogleFontsResponse';

// Allowed fonts from the existing system
const ALLOWED_FONTS = [
  'Anton',
  'Archivo',
  'Barlow',
  'Bebas Neue',
  'Bitter',
  'Bricolage Grotesque',
  'Caprasimo',
  'DM Sans',
  'DM Serif Text',
  'EB Garamond',
  'Exo 2',
  'Inter',
  'Lato',
  'Lexend',
  'Libre Baskerville',
  'Lora',
  'Manrope',
  'Merriweather',
  'Montserrat',
  'Noto Sans',
  'Noto Serif',
  'Nunito Sans',
  'Open Sans',
  'Oswald',
  'Outfit',
  'Playfair Display',
  'Poppins',
  'PT Sans',
  'Quicksand',
  'Raleway',
  'Roboto',
  'Source Sans Pro',
  'Space Grotesk',
  'Titillium Web',
  'Ubuntu',
  'Work Sans',
];

// Function to fetch Google Fonts data for all allowed fonts
export const fetchGoogleFonts = async (apiKey: string): Promise<FontDictionary> => {
  const url = `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}`;

  const response = await fetch(url, { signal: AbortSignal.timeout(10000) });

  if (!response.ok) {
    throw new Error(`Google Fonts API returned status ${response.status}`);
  }

  const rawData = await response.json();

  const data = plainToClass(GoogleFontsResponse, rawData);
  const errors = await validate(data);

  if (errors.length > 0) {
    throw new Error(
      `Invalid response from Google Fonts API: ${errors.map((e) => e.toString()).join(', ')}`,
    );
  }

  // Filter only allowed fonts and convert to our format
  const filteredFonts = data.items.filter((font: { family: string }) =>
    ALLOWED_FONTS.includes(font.family),
  );

  // Convert to our FontDictionary format
  const fontDictionary: FontDictionary = {};

  filteredFonts.forEach((font: { family: string; files?: Record<string, string> }) => {
    if (!font.files) {
      fontDictionary[font.family] = {
        family: font.family,
        variants: [],
      };
      return;
    }

    const variants = Object.keys(font.files).reduce(
      (acc, variant) => {
        const weight = deriveWeight(variant);
        const style = deriveStyle(variant);

        const variantMap = {
          '100': 'thin',
          '200': 'extra-light',
          '300': 'light',
          '400': 'regular',
          '500': 'medium',
          '600': 'semi-bold',
          '700': 'bold',
          '800': 'extra-bold',
          '900': 'black',
        } as const;
        const mappedVariant = variantMap[weight as keyof typeof variantMap];

        // Only add if it maps to a supported variant
        if (mappedVariant) {
          const variantObj = {
            variant: mappedVariant,
            weight,
            style,
            url: font.files?.[variant] || '',
          };

          // Check for duplicates before adding
          const isDuplicate = acc.some(
            (v) => v.variant === variantObj.variant && v.style === variantObj.style,
          );

          if (!isDuplicate) {
            acc.push(variantObj);
          }
        }

        return acc;
      },
      [] as Array<{ variant: string; weight: string; style: string; url: string }>,
    );

    fontDictionary[font.family] = {
      family: font.family,
      variants,
    };
  });

  return fontDictionary;
};
